"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const qrCodePointsCollectionInputs = [
  {
    name: "backgroundType",
    type: "string",
    enum: ["image", "video"],
    required: true,
    defaultValue: "image",
  },
  {
    name: "logo",
    type: "file",
    required: true,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F1ee41566ad9d4c978d68fd13d262bcec",
    allowedFileTypes: IMAGE_FILE_TYPES,
  },
  {
    name: "logoWidth",
    type: "number",
  },
  {
    name: "logoHeight",
    type: "number",
  },
  {
    name: "content",
    type: "object",
    defaultValue: {
      title: "Sign in to earn points",
      titleLogged: "You've earned {#pointsEarned#} Ploom  Club Points!",
      text: "<p>Thank you so much for participating in our event! We'd love to show our appreciation by awarding you Ploom Club Points. Sign in now or create an account in moments to receive your reward.</p>\r\n",
      textLogged:
        "<p>Thank you so much for participating in our event and being part of our community! To show our appreciation, we've awarded Ploom Club Points to your account.</p>\r\n",
      cta: [
        {
          link: "#",
          additionalBehavior: "onlyForNotLoggedIn",
          label: "Sign in",
          theme: "dark",
          layout: "border",
          openIn: "sameTab",
          tracking: null,
          target: "",
        },
      ],
      titleCollected: "Points have already been awarded to you",
      textCollected:
        "<p>Thank you so much for participating in our event! We appreciate your enthusiasm, but you have already been awarded Ploom Club Points for scanning this QR code. Your balance should be updated shortly.</p>\r\n",
    },
    required: true,
    subFields: [
      {
        name: "tagLine",
        type: "string",
      },
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "titleLogged",
        friendlyName: "Title for logged in users",
        type: "string",
        helperText: "Supports {#pointsEarned#} placeholder",
        required: true,
      },
      {
        name: "titleCollected",
        friendlyName: "Title for logged in users who already collected points",
        type: "string",
        required: true,
      },
      {
        name: "text",
        type: "richText",
        friendlyName: "Description",
        required: true,
      },
      {
        name: "textLogged",
        type: "richText",
        friendlyName: "Description for logged in users",
        required: true,
      },
      {
        name: "textCollected",
        friendlyName:
          "Description for logged in users who already collected points",
        type: "richText",
        required: true,
      },
      {
        name: "subheadline",
        type: "string",
      },
      {
        name: "subheadlineLogged",
        type: "string",
      },
      {
        name: "cta",
        type: "list",
        required: true,
        subFields: [
          {
            name: "additionalBehavior",
            type: "string",
            defaultValue: "always",
            required: true,
            enum: ["always", "onlyForLoggedIn", "onlyForNotLoggedIn"],
          },
          {
            name: "label",
            type: "string",
            required: true,
          },
          {
            name: "theme",
            type: "string",
            defaultValue: "light",
            enum: ["light", "dark"],
            required: true,
          },
          {
            name: "layout",
            type: "string",
            enum: ["chevron", "full"],
            required: true,
          },
          {
            name: "tracking",
            type: "string",
            required: true,
          },
          {
            name: "link",
            type: "url",
            required: true,
          },
          {
            name: "openIn",
            friendlyName: "Open in",
            type: "string",
            enum: ["new Tab", "same Tab"],
            onChange: `options.set("target", (options.get("openIn") === "new Tab"? "_blank" : ""))`,
          },
          {
            hideFromUI: true,
            name: "target",
            type: "string",
            enum: ["", "_blank"],
            required: true,
            defaultValue: "",
          },
        ],
      },
    ],
  },
  {
    name: "desktopImage",
    type: "object",
    showIf: `options.get("backgroundType") === "image"`,
    defaultValue: {
      src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fb743746aa145432e83aa7a7011e53452",
      title: "Desktop background",
      alt: "Desktop background",
    },
    subFields: [
      {
        name: "src",
        type: "file",
        required: true,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "alt",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "mediumImage",
    type: "object",
    showIf: `options.get("backgroundType") === "image"`,
    defaultValue: {
      src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fb743746aa145432e83aa7a7011e53452",
      title: "Tablet background",
      alt: "Tablet background",
    },
    subFields: [
      {
        name: "src",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
      },
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "alt",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "smallImage",
    type: "object",
    showIf: `options.get("backgroundType") === "image"`,
    defaultValue: {
      src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fb743746aa145432e83aa7a7011e53452",
      title: "Mobile background",
      alt: "Mobile background",
    },
    subFields: [
      {
        name: "src",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
      },
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "alt",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "videoFileReference",
    type: "file",
    allowedFileTypes: VIDEO_FILE_TYPES,
    showIf: `options.get("backgroundType") === "video"`,
  },
  {
    name: "tabletVideoFileReference",
    type: "file",
    allowedFileTypes: VIDEO_FILE_TYPES,
    showIf: `options.get("backgroundType") === "video"`,
  },
  {
    name: "mobileVideoFileReference",
    type: "file",
    allowedFileTypes: VIDEO_FILE_TYPES,
    showIf: `options.get("backgroundType") === "video"`,
  },
  {
    name: "legalDisclaimer",
    type: "string",
  },
] as const satisfies Inputs;

export const QrCodePointsCollectionComponent: RegisteredComponent = {
  name: "QrCodePointsCollection",
  component: dynamic(async () =>
    withValidation(
      qrCodePointsCollectionInputs,
      (await import("./QrCodePointsCollection")).QrCodePointsCollection,
      "QrCodePointsCollection"
    )
  ),
  inputs: qrCodePointsCollectionInputs,
};
